import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#syncCartState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail, cart }) => {
        // If drawer was empty or cart-items-component was not present in drawer, morph section
        const wasEmpty = this.#isCartEmpty() || !this.querySelector('cart-items-component');
        if (wasEmpty && detail?.sections?.['cart-drawer-section']) {
          try {
            const { morphSection } = await import('@theme/section-renderer');
            await morphSection('cart-drawer-section', detail.sections['cart-drawer-section'], { mode: 'hydration' });
          } catch (e) {
            console.warn('[cart-drawer] Failed to morph section:', e);
          }
        } else if (wasEmpty && !detail?.didError) {
          try {
            const { sectionRenderer } = await import('@theme/section-renderer');
            await sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration' });
          } catch (e) {
            console.warn('[cart-drawer] Failed to render section:', e);
          }
        }

        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#updateFreeShippingBar(cart, detail);
          });
        };

        this.#updateFreeShippingBar(cart, detail);

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Calculates total price in cents from various cart object schemas
   * @param {Record<string, any>} [cart]
   * @param {Record<string, any>} [detail]
   * @returns {number}
   */
  #extractCartTotal(cart, detail) {
    if (detail?.totalPrice !== undefined && typeof detail.totalPrice === 'number') {
      return detail.totalPrice;
    }
    if (detail?.rawCart?.total_price !== undefined) {
      return detail.rawCart.total_price;
    }
    if (cart?.total_price !== undefined) {
      return cart.total_price;
    }
    if (cart?.cost?.totalAmount?.amount !== undefined) {
      return Math.round(parseFloat(cart.cost.totalAmount.amount) * 100);
    }
    if (cart?.totalPrice?.amount !== undefined) {
      return Math.round(parseFloat(cart.totalPrice.amount) * 100);
    }
    if (typeof cart?.totalPrice === 'number') {
      return cart.totalPrice > 1000 ? cart.totalPrice : Math.round(cart.totalPrice * 100);
    }
    return 0;
  }

  /**
   * Calculates item count from various cart schemas
   * @param {Record<string, any>} [cart]
   * @param {Record<string, any>} [detail]
   * @returns {number}
   */
  #extractCartItemCount(cart, detail) {
    if (detail?.itemCount !== undefined && typeof detail.itemCount === 'number') {
      return detail.itemCount;
    }
    if (detail?.rawCart?.item_count !== undefined) {
      return detail.rawCart.item_count;
    }
    if (cart?.item_count !== undefined) {
      return cart.item_count;
    }
    if (cart?.totalQuantity !== undefined) {
      return cart.totalQuantity;
    }
    if (Array.isArray(cart?.items)) {
      return cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    }
    if (Array.isArray(cart?.lines)) {
      return cart.lines.reduce((sum, line) => sum + (line.quantity || 0), 0);
    }
    return 0;
  }

  /**
   * Background sync to fetch fresh cart data from /cart.js
   */
  #syncCartState = async () => {
    try {
      const res = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (res.ok) {
        const cartData = await res.json();
        this.#updateFreeShippingBar(cartData);
      }
    } catch (e) {
      // Ignore background sync errors
    }
  };

  /**
   * @param {Record<string, any>} [cart]
   * @param {Record<string, any>} [detail]
   */
  #updateFreeShippingBar = (cart, detail) => {
    const banner = this.querySelector('.cart-drawer-shipping-banner');
    if (!banner) return;

    const threshold = parseInt(banner.getAttribute('data-shipping-threshold') || '5000', 10);
    const total = this.#extractCartTotal(cart, detail);
    const remaining = Math.max(0, threshold - total);
    const percent = Math.min(100, Math.max(0, (total / threshold) * 100));

    const bar = banner.querySelector('.cart-drawer-shipping-banner__bar');
    if (bar instanceof HTMLElement) bar.style.width = `${percent}%`;

    const textEl = banner.querySelector('.cart-drawer-shipping-banner__text');
    if (textEl) {
      if (remaining > 0) {
        const formatted = `$${(remaining / 100).toFixed(2)}`;
        textEl.innerHTML = `You’re <span class="cart-drawer-shipping-highlight">${formatted}</span> away from FREE SHIPPING!`;
      } else {
        textEl.innerHTML = `🎉 You’ve unlocked <strong>FREE SHIPPING!</strong>`;
      }
    }

    const countEl = this.querySelector('.cart-drawer-custom-title__count');
    const itemCount = this.#extractCartItemCount(cart, detail);
    if (countEl && itemCount > 0) {
      countEl.textContent = itemCount === 1 ? '(1 item)' : `(${itemCount} items)`;
    }
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
